// pages/event/blogs/Blogs.jsx
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
    getData,
    addData,
    updateData,
    deleteData,
    getprogramManager,
    getsubdomainType,
} from '../../../services/event/blogs/blogsServices';
import { Modal, Button, Form } from 'react-bootstrap';
import DataTable from 'react-data-table-component';
import Header  from '../../../components/Layout/Header';
import Footer  from '../../../components/Layout/Footer';
import Sidebar from '../../../components/Layout/Sidebar';
import Spinner from '../../../components/Spinner';
import Swal    from 'sweetalert2';
import { validateImageFile }   from '../../../utils/fileValidation';
import { isQuillContentEmpty, cleanQuillHtml } from '../../../utils/quill_helper';
import ReactQuill from "react-quill";
// import "react-quill/dist/quill.snow.css";
  

const subDomain = localStorage.getItem('sub_domain') || '';

// ── remarks attribution (event side): always tagged as 'event - <subDomain>' ──
const currentUserName = `event - ${subDomain}`;

const EMPTY_FORM = {
    id:           '',
    sub_domain:   subDomain,
    title:        '',
    description:  '',
    author:       '',
    alt_name:     '',
    subject:      '',
    approval:     false,
    uploadedfile: null,
    remarks:      '',
    meta_title:   '',
    meta_description: '',
    remarks_history: [],
};

const quillModules = {
    toolbar: [
        [{ font: [] }, { size: ['small', false, 'large', 'huge'] }],
        [{ header: [1, 2, 3, 4, 5, 6, false] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ script: 'sub' }, { script: 'super' }],
        ['blockquote', 'code-block'],
        [{ color: [] }, { background: [] }],
        [{ align: [] }],
        [{ list: 'ordered' }, { list: 'bullet' }],
        [{ indent: '-1' }, { indent: '+1' }],
        ['link'],
        ['clean'],
    ],
};
const quillFormats = [
    'font','size','header',
    'bold','italic','underline','strike','script',
    'blockquote','code-block',
    'color','background','align',
    'list','bullet','indent','link',
];

// ── helper: human readable date for the preview ─────────────────────────────
const formatPreviewDate = (d) => {
    const date = d ? new Date(d) : new Date();
    return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

// ── Quill preview styles — maps ql-* classes so formatting reflects correctly ──
const QUILL_PREVIEW_STYLES = `

 .blog-preview-body p      
 { margin-bottom: 0; }
    .ql-editor {
        height: 200px;
        overflow-y: auto;
    }}
`;

const Blogs = () => {
    const [dataList,        setDataList]        = useState([]);
    const [form,            setForm]            = useState(EMPTY_FORM);
    const [view,            setView]            = useState('list');
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [selectedRow,     setSelectedRow]     = useState(null);
    const [searchText,      setSearchText]      = useState('');
    const [currentPage,     setCurrentPage]     = useState(1);
    const [loading,         setLoading]         = useState(true);
    const [errors,          setErrors]          = useState({});
    const [imgPreview,      setImgPreview]      = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [defaultAuthor,  setDefaultAuthor]  = useState('');
    const [defaultSubject, setDefaultSubject] = useState('');

    // ── fetch blog list ────────────────────────────────────────────────────
    const fetchData = useCallback(async () => {
        try {
            const data = await getData({ sub_domain: subDomain });
            setDataList(data || []);
        } catch (err) {
            console.error('Failed to fetch blogs:', err);
        }
    }, []);

    // ── init ───────────────────────────────────────────────────────────────
    useEffect(() => {
        const init = async () => {
            try {
                setLoading(true);
                await fetchData();

                let resolvedAuthor = '';
                const pmData = await getprogramManager();
                if (pmData?.length > 0) {
                    const matched = pmData.find(item => item.sub_domain === subDomain);
                    if (matched) resolvedAuthor = matched.coordinator_name || '';
                }

                let resolvedSubject = '';
                const sdData = await getsubdomainType({ sub_domain: subDomain });
                if (sdData?.length > 0) {
                    const matched = sdData.find(item => item.sub_domain === subDomain);
                    if (matched) resolvedSubject = matched.subject || '';
                }

                setDefaultAuthor(resolvedAuthor);
                setDefaultSubject(resolvedSubject);
                setForm(prev => ({ ...prev, author: resolvedAuthor, subject: resolvedSubject }));

            } catch (err) {
                console.error('Init error:', err);
            } finally {
                setLoading(false);
            }
        };
        init();
    }, [fetchData]);

    // ── open inline form ───────────────────────────────────────────────────
    const openForm = (type, data = null) => {
        setErrors({});
        setSubmitting(false);

        if (type === 'add') {
            setForm({
                ...EMPTY_FORM,
                sub_domain:      subDomain,
                author:          defaultAuthor,
                subject:         defaultSubject,
                remarks:         '',
                remarks_history: [],
            });
            setImgPreview(null);
        } else {
            let parsedHistory = data.remarks_history || [];
            if (typeof parsedHistory === 'string') {
                try { parsedHistory = JSON.parse(parsedHistory); } catch { parsedHistory = []; }
            }
            console.log('[Blogs] edit row remarks_history raw:',    data.remarks_history);
            console.log('[Blogs] edit row remarks_history parsed:', parsedHistory);
            setForm({
                ...EMPTY_FORM,
                ...data,
                sub_domain:      subDomain,
                approval:        Number(data.approval) === 1,
                uploadedfile:    null,
                remarks:         '',
                remarks_history: parsedHistory,
            });
            setImgPreview(data?.blog_img || null);
        }

        setView(type);
    };

    const closeForm = () => {
        setView('list');
        setErrors({});
        setSubmitting(false);
    };

    // ── file picker ────────────────────────────────────────────────────────
    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        validateImageFile(file, 'blog')
            .then(() => {
                setForm(prev => ({ ...prev, uploadedfile: file }));
                setErrors(prev => ({ ...prev, uploadedfile: '' }));
                setImgPreview(URL.createObjectURL(file));
            })
            .catch(err => {
                setForm(prev => ({ ...prev, uploadedfile: null }));
                setErrors(prev => ({ ...prev, uploadedfile: err }));
                setImgPreview(null);
                e.target.value = '';
            });
    };

    // ── text field change ──────────────────────────────────────────────────
    // ── text field change ──────────────────────────────────────────────────
const handleChange = (e) => {
    const { name, value } = e.target;
    const val = name === 'approval' ? value === '1' : value;

    setForm(prev => ({ ...prev, [name]: val }));

    let fieldError = '';
    if (name === 'meta_title') {
        if (!value.trim()) {
            fieldError = 'Meta title is required';
        } else if (value.length < 20 || value.length > 70) {
            fieldError = 'Meta Title should be between 20 and 70 characters';
        }
    } else if (name === 'meta_description') {
        if (!value.trim()) {
            fieldError = 'Meta description is required';
        } else if (value.length < 70 || value.length > 160) {
            fieldError = 'Meta Description should be between 70 and 160 characters';
        }
    } else if (name === 'title') {
        fieldError = value.trim() ? '' : 'Title is required';
    } else if (name === 'alt_name') {
        fieldError = value.trim() ? '' : 'Alt name is required';
    }

    setErrors(prev => ({ ...prev, [name]: fieldError }));
};

    // ── quill ──────────────────────────────────────────────────────────────
const handleDescriptionChange = (value) => {
    setForm(prev => ({ ...prev, description: value }));

    let descError = '';
    if (isQuillContentEmpty(value)) {
        descError = 'Description is required';
    } else {
        const wordCount = getWordCount(value);
        if (wordCount > MAX_DESCRIPTION_WORDS) {
            descError = `Description exceeds the ${MAX_DESCRIPTION_WORDS} word limit (current: ${wordCount} words)`;
        }
    }

    setErrors(prev => ({ ...prev, description: descError }));
    };

    // ── validation ─────────────────────────────────────────────────────────
    const validateForm = () => {
    const newErrors = {};
    if (!form.title.trim())  
      newErrors.title       = 'Title is required';
    if (isQuillContentEmpty(form.description)) {
        newErrors.description = 'Description is required';
    } else {
        const wordCount = getWordCount(form.description);
        if (wordCount > MAX_DESCRIPTION_WORDS) {
            newErrors.description = `Description exceeds the ${MAX_DESCRIPTION_WORDS} word limit (current: ${wordCount} words)`;
        }
    }
    if (!form.alt_name.trim())                 
        newErrors.alt_name    = 'Alt name is required';
    if (!form.meta_title.trim()) {
        newErrors.meta_title = 'Meta title is required';
    } else if (form.meta_title.length < 20 || form.meta_title.length > 70) {
        newErrors.meta_title = 'Meta Title should be between 20 and 70 characters';
    }

    if (!form.meta_description.trim()) {
        newErrors.meta_description = 'Meta description is required';
    } else if (form.meta_description.length < 70 || form.meta_description.length > 160) {
        newErrors.meta_description = 'Meta Description should be between 70 and 160 characters';
    }

    if (view === 'add' && !form.uploadedfile) newErrors.uploadedfile = 'Blog image is required';
    if (view === 'edit' && !form.remarks.trim()) newErrors.remarks = 'Remarks is required';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
    };

    // ── helper: word count for Quill HTML content ──────────────────────────────
    const getWordCount = (html) => {
        if (!html) return 0;
        const text = html.replace(/<[^>]*>/g, ' ').replace(/&nbsp;/g, ' ').trim();
        if (!text) return 0;
        return text.split(/\s+/).filter(Boolean).length;
    };

    const MAX_DESCRIPTION_WORDS = 8000;
    // ── submit ─────────────────────────────────────────────────────────────
    const handleSubmit = async () => {
        if (!validateForm()) return;
        setSubmitting(true);
        const formData = new FormData();
        formData.append('id',               form.id || '');
        formData.append('sub_domain',       form.sub_domain);
        formData.append('title',            form.title);
        formData.append('description', cleanQuillHtml(form.description));        
        formData.append('author',           form.author  || '');
        formData.append('subject',          form.subject || '');
        formData.append('alt_name',         form.alt_name);
        formData.append('meta_title',       form.meta_title);
        formData.append('meta_description', form.meta_description);
        formData.append('approval',         0);

        if (form.uploadedfile instanceof File) {
            formData.append('uploadedfile', form.uploadedfile);
        }

        if (view === 'edit') {
            const updatedHistory = [
                ...(form.remarks_history || []),
                {
                    remark:    form.remarks.trim(),
                    timestamp: new Date().toISOString(),
                    user:      currentUserName,
                },
            ];
            formData.append('remarks_history', JSON.stringify(updatedHistory));
        }

        try {
            if (view === 'add') {
                await addData(formData);
            } else {
                await updateData(formData);
            }
            fetchData();
            closeForm();
            Swal.fire({
                icon: 'success', title: 'Success',
                text: `Blog ${view === 'add' ? 'added' : 'updated'} successfully!`,
                timer: 2000, showConfirmButton: false,
            });
        } catch (err) {
            console.error('Submit error:', err);
            Swal.fire({ icon: 'error', title: 'Error', text: 'Something went wrong!' });
        }
    };

    // ── delete ─────────────────────────────────────────────────────────────
    const handleDelete = async () => {
        try {
            await deleteData(selectedRow.id);
            fetchData();
            setShowDeleteModal(false);
            Swal.fire({
                icon: 'success', title: 'Deleted',
                text: 'Blog deleted successfully!',
                timer: 2000, showConfirmButton: false,
            });
        } catch (err) {
            Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to delete blog.' });
        }
    };

    // ── filtered list ──────────────────────────────────────────────────────
    const filteredData = useMemo(() => {
        return dataList
            .filter(d =>
                (d.title  || '').toLowerCase().includes(searchText.toLowerCase()) ||
                (d.author || '').toLowerCase().includes(searchText.toLowerCase())
            )
            .sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt));
    }, [dataList, searchText]);

    // ── columns ────────────────────────────────────────────────────────────
    const columns = [
        {
            name: '#',
            selector: (_, i) => (currentPage - 1) * 10 + (i + 1),
            width: '60px',
        },
        {
            name: 'Image',
            cell: row => row.blog_img
                ? <img src={row.blog_img} alt={row.alt_name || 'blog'}
                       style={{ width: 64, height: 40, objectFit: 'cover', borderRadius: 4 }} />
                : <span className="text-muted small">—</span>,
            width: '90px',
        },
        { name: 'Title',   selector: row => row.title,          sortable: true, wrap: true, grow: 2 },
        { name: 'Author',  selector: row => row.author  || '—', sortable: true, wrap: true },
        { name: 'Subject', selector: row => row.subject || '—', sortable: true, wrap: true },
        {
            name: 'Approval',
            cell: row => (
                <span className={`badge ${Number(row.approval) === 1 ? 'bg-success' : 'bg-danger'}`}>
                    {Number(row.approval) === 1 ? 'Active' : 'Inactive'}
                </span>
            ),
            width: '100px',
        },
        {
            name: 'Actions',
            cell: row => (
                <div className="d-flex gap-1">
                    <Button variant="warning" size="sm" onClick={() => openForm('edit', row)}>Edit</Button>
                    <Button variant="danger"  size="sm"
                        onClick={() => { setSelectedRow(row); setShowDeleteModal(true); }}>
                        Delete
                    </Button>
                </div>
            ),
            width: '150px',
        },
    ];

    const isFormView = view === 'add' || view === 'edit';

    // ── render ─────────────────────────────────────────────────────────────
    return (
        <div className="d-flex">
            <Sidebar />
            <div className="main-content w-100">
                <Header />

                {/* ── Inject Quill preview + editor scroll styles once ── */}
                <style>{QUILL_PREVIEW_STYLES}</style>

                {loading ? (
                    <div className="d-flex justify-content-center align-items-center w-100"
                         style={{ minHeight: '300px' }}>
                        <Spinner />
                    </div>
                ) : (
                    <div>
                        <div className="container-fluid p-4">

                            {!isFormView ? (
                                <>
                                    {/* ════ LIST VIEW ════ */}
                                    <div className="row mb-3">
                                        <div className="col-8">
                                            <p className="fs-4 text-dark mb-0">Blogs Management</p>
                                        </div>
                                        <div className="col-4 text-end">
                                            <button className="btn btn-primary" onClick={() => openForm('add')}>
                                                Add Blog
                                            </button>
                                        </div>
                                    </div>

                                    <DataTable
                                        columns={columns}
                                        data={filteredData}
                                        pagination
                                        highlightOnHover
                                        responsive
                                        striped
                                        paginationPerPage={10}
                                        paginationRowsPerPageOptions={[10, 20, 50, 100, 200, 500]}
                                        onChangePage={setCurrentPage}
                                        subHeader
                                        subHeaderComponent={
                                            <input
                                                className="form-control"
                                                style={{ maxWidth: 260 }}
                                                type="text"
                                                placeholder="Search by title or author…"
                                                value={searchText}
                                                onChange={e => setSearchText(e.target.value)}
                                            />
                                        }
                                    />
                                </>
                            ) : (
                                <>
                                    {/* ════ ADD / EDIT INLINE VIEW ════ */}
                                    <div className="row mb-3 align-items-center">
                                        <div className="col-12">
                                            <button
                                                className="btn btn-link ps-0 text-decoration-none"
                                                onClick={closeForm}
                                            >
                                                ← Back to list
                                            </button>
                                            <p className="fs-4 text-dark mb-0">
                                                {view === 'add' ? 'Add Blog' : 'Edit Blog'}
                                            </p>
                                        </div>
                                    </div>

                                    <div className="row g-4">

                                        {/* ── LEFT: FORM ───────────────────────────────────── */}
                                        <div className="col-lg-6">
                                            <div className="card shadow-sm">
                                                <div className="card-body">
                                                    <Form>
                                                        <div className="row g-3">

                                                            {/* Sub Domain */}
                                                            <Form.Group className="col-md-6">
                                                                <Form.Label className="fs-5 text-dark">Sub Domain</Form.Label>
                                                                <Form.Control type="text" value={form.sub_domain} readOnly />
                                                            </Form.Group>

                                                            {/* Title */}
                                                            <Form.Group className="col-md-6">
                                                                <Form.Label className="fs-5 text-dark">
                                                                    Title <span className="text-danger">*</span>
                                                                </Form.Label>
                                                                <Form.Control
                                                                    type="text" name="title"
                                                                    value={form.title} onChange={handleChange}
                                                                    isInvalid={!!errors.title}
                                                                    placeholder="Enter blog title"
                                                                />
                                                                <Form.Control.Feedback type="invalid">
                                                                    {errors.title}
                                                                </Form.Control.Feedback>
                                                            </Form.Group>

                                                            {/* Author */}
                                                            <Form.Group className="col-md-6">
                                                                <Form.Label className="fs-5 text-dark">
                                                                    Author
                                                                    <small className="text-muted ms-1">(auto-filled)</small>
                                                                </Form.Label>
                                                                <Form.Control
                                                                    type="text" name="author"
                                                                    value={form.author} readOnly
                                                                    style={{ backgroundColor: '#f8f9fa' }}
                                                                />
                                                            </Form.Group>

                                                            {/* Subject */}
                                                            <Form.Group className="col-md-6">
                                                                <Form.Label className="fs-5 text-dark">
                                                                    Subject
                                                                    <small className="text-muted ms-1">(auto-filled)</small>
                                                                </Form.Label>
                                                                <Form.Control
                                                                    type="text" name="subject"
                                                                    value={form.subject} readOnly
                                                                    style={{ backgroundColor: '#f8f9fa' }}
                                                                />
                                                            </Form.Group>

                                                            {/* Blog Image */}
                                                            <Form.Group className="col-md-6">
                                                                <Form.Label className="fs-5 text-dark">
                                                                    Blog Image
                                                                    {view === 'add' && <span className="text-danger"> *</span>}
                                                                    <small className="text-primary ms-1">
                                                                        (1200×630 | 10 kb – 100 kb)
                                                                    </small>
                                                                </Form.Label>
                                                                <Form.Control
                                                                    type="file"
                                                                    name="uploadedfile"
                                                                    accept="image/*"
                                                                    onChange={handleFileChange}
                                                                    isInvalid={!!errors.uploadedfile}
                                                                />
                                                                <Form.Control.Feedback type="invalid">
                                                                    {errors.uploadedfile}
                                                                </Form.Control.Feedback>
                                                            </Form.Group>

                                                            {/* Alt Name */}
                                                            <Form.Group className="col-md-6">
                                                                <Form.Label className="fs-5 text-dark">
                                                                    Alt Name <span className="text-danger">*</span>
                                                                </Form.Label>
                                                                <Form.Control
                                                                    type="text" name="alt_name"
                                                                    value={form.alt_name} onChange={handleChange}
                                                                    isInvalid={!!errors.alt_name}
                                                                    placeholder="Image alt text for SEO"
                                                                />
                                                                <Form.Control.Feedback type="invalid">
                                                                    {errors.alt_name}
                                                                </Form.Control.Feedback>
                                                            </Form.Group>

                                                            {/* Description — fixed height scroll via .quill-fixed class */}
                                                            <Form.Group className="col-12">
                                                                <Form.Label className="fs-5 text-dark">
                                                                    Description <span className="text-danger">*</span>
                                                                </Form.Label>
                                                                
                                                                <ReactQuill
                                                                    value={form.description}
                                                                    onChange={handleDescriptionChange}
                                                                    theme="snow"
                                                                    modules={quillModules}
                                                                    formats={quillFormats}
                                                                    placeholder="Enter blog content…"
                                                                    className={
                                                                        (errors.description ? ' border border-danger rounded' : '')
                                                                    }
                                                                />
                                                                {errors.description && (
                                                                    <div className="invalid-feedback d-block mt-1">
                                                                        {errors.description}
                                                                    </div>
                                                                )}
                                                            </Form.Group>

                                                            {/* Meta Title */}
                                                            <Form.Group className="col-md-6">
                                                                <Form.Label className="fs-5 text-dark">
                                                                    Meta Title <span className="text-danger">*</span>
                                                                    <small className="text-primary ms-1">
                                                                        Char Limit: 20–70 | Current: {form.meta_title.length}
                                                                    </small>
                                                                </Form.Label>
                                                                <Form.Control
                                                                    as="textarea" rows={8}
                                                                    name="meta_title"
                                                                    value={form.meta_title}
                                                                    onChange={handleChange}
                                                                    isInvalid={!!errors.meta_title}
                                                                />
                                                                <Form.Control.Feedback type="invalid">
                                                                    {errors.meta_title}
                                                                </Form.Control.Feedback>
                                                            </Form.Group>

                                                            {/* Meta Description */}
                                                            <Form.Group className="col-md-6">
                                                                <Form.Label className="fs-5 text-dark">
                                                                    Meta Description <span className="text-danger">*</span>
                                                                    <small className="text-primary ms-1">
                                                                        Char Limit: 70–160 | Current: {form.meta_description.length}
                                                                    </small>
                                                                </Form.Label>
                                                                <Form.Control
                                                                    as="textarea" rows={8}
                                                                    name="meta_description"
                                                                    value={form.meta_description}
                                                                    onChange={handleChange}
                                                                    isInvalid={!!errors.meta_description}
                                                                />
                                                                <Form.Control.Feedback type="invalid">
                                                                    {errors.meta_description}
                                                                </Form.Control.Feedback>
                                                            </Form.Group>

                                                            {/* Remarks — edit only */}
                                                            {view === 'edit' && (
                                                                <>
                                                                    {/* Remarks History */}
                                                                    <Form.Group className="col-12">
                                                                        <Form.Label className="fs-5 text-dark">
                                                                            Remarks History
                                                                        </Form.Label>
                                                                        {form.remarks_history?.length > 0 ? (
                                                                            <div style={{
                                                                                maxHeight: 200,
                                                                                overflowY: 'auto',
                                                                                border: '1px solid #dee2e6',
                                                                                borderRadius: 6,
                                                                                padding: '8px 12px',
                                                                                background: '#f8f9fa',
                                                                            }}>
                                                                                {[...form.remarks_history].reverse().map((item, idx) => (
                                                                                    <div key={idx} style={{
                                                                                        borderBottom: idx < form.remarks_history.length - 1
                                                                                            ? '1px solid #dee2e6' : 'none',
                                                                                        padding: '6px 0',
                                                                                    }}>
                                                                                        <div className="d-flex justify-content-between align-items-baseline">
                                                                                            <small className="text-muted">
                                                                                                {new Date(item.timestamp).toLocaleString('en-IN', {
                                                                                                    day: '2-digit', month: 'short', year: 'numeric',
                                                                                                    hour: '2-digit', minute: '2-digit',
                                                                                                })}
                                                                                            </small>
                                                                                            {item.user && (
                                                                                                <span className="badge bg-info text-dark" style={{ fontSize: 10 }}>
                                                                                                    {item.user}
                                                                                                </span>
                                                                                            )}
                                                                                        </div>
                                                                                        <p className="mb-0 mt-1" style={{ fontSize: 14 }}>
                                                                                            {item.remark}
                                                                                        </p>
                                                                                    </div>
                                                                                ))}
                                                                            </div>
                                                                        ) : (
                                                                            <div className="text-muted small border rounded p-2 bg-light">
                                                                                No remarks history yet.
                                                                            </div>
                                                                        )}
                                                                    </Form.Group>

                                                                    {/* New remark input */}
                                                                    <Form.Group className="col-12">
                                                                        <Form.Label className="fs-5 text-dark">
                                                                            Remarks <span className="text-danger">*</span>
                                                                            <small className="text-muted ms-1">
                                                                                (will be saved as {currentUserName})
                                                                            </small>
                                                                        </Form.Label>
                                                                        <Form.Control
                                                                            as="textarea"
                                                                            rows={2}
                                                                            name="remarks"
                                                                            value={form.remarks}
                                                                            onChange={handleChange}
                                                                            isInvalid={!!errors.remarks}
                                                                            placeholder="Enter reason for this edit…"
                                                                        />
                                                                        <Form.Control.Feedback type="invalid">
                                                                            {errors.remarks}
                                                                        </Form.Control.Feedback>
                                                                    </Form.Group>
                                                                </>
                                                            )}

                                                        </div>{/* /row g-3 */}

                                                        <div className="d-flex justify-content-end gap-2 mt-4">
                                                        <Button variant="secondary" onClick={closeForm} disabled={submitting}>
                                                            Cancel
                                                        </Button>
                                                        <Button variant="primary" onClick={handleSubmit} disabled={submitting}>
                                                            {submitting ? (
                                                                <>
                                                                    <span
                                                                        className="spinner-border spinner-border-sm me-2"
                                                                        role="status"
                                                                        aria-hidden="true"
                                                                    ></span>
                                                                    {view === 'add' ? 'Adding…' : 'Saving…'}
                                                                </>
                                                            ) : (
                                                                view === 'add' ? 'Add' : 'Save'
                                                            )}
                                                        </Button>
                                                    </div>
                                                    </Form>
                                                </div>
                                            </div>
                                        </div>{/* /col-lg-6 left */}

                                        {/* ── RIGHT: LIVE FULL-POST PREVIEW ───────────────── */}
                                        <div className="col-lg-6">
                                            <div className="mb-2 d-flex align-items-center justify-content-between">
                                                <span className="text-muted small text-uppercase fw-semibold">
                                                    Live Preview
                                                </span>
                                                <span className="badge bg-light text-dark border">
                                                    How this looks on the site
                                                </span>
                                            </div>

                                            <div
                                                className="card shadow-sm"
                                                style={{ position: 'sticky', top: 16 }}
                                            >
                                                {/* Hero image */}
                                                <div style={{
                                                    width: '100%',
                                                    aspectRatio: '1200 / 630',
                                                    background: '#eef1f4',
                                                    overflow: 'hidden',
                                                    borderRadius: '0.375rem 0.375rem 0 0',
                                                }}>
                                                    {imgPreview ? (
                                                        <img
                                                            src={imgPreview}
                                                            alt={form.alt_name || 'Blog preview'}
                                                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                                        />
                                                    ) : (
                                                        <div className="d-flex h-100 w-100 align-items-center justify-content-center text-muted">
                                                            No image uploaded yet
                                                        </div>
                                                    )}
                                                </div>

                                                <div className="card-body">
                                                    {/* Meta row */}
                                                    <div className="d-flex flex-wrap gap-3 text-muted small mb-2">
                                                        {form.subject && (
                                                            <span className="badge bg-primary-subtle text-primary">
                                                                {form.subject}
                                                            </span>
                                                        )}
                                                        <span>{formatPreviewDate(form.updatedAt)}</span>
                                                        {form.author && <span>By {form.author}</span>}
                                                    </div>

                                                    {/* Title */}
                                                    <h2 className="fw-bold">
                                                        {form.title || (
                                                            <span className="text-muted fst-italic">
                                                                Your blog title will appear here
                                                            </span>
                                                        )}
                                                    </h2>

                                                    {/* Body — capped height + scroll, Quill output styles applied via QUILL_PREVIEW_STYLES */}
                                                    <div
                                                        className="blog-preview-body"
                                                        style={{
                                                            lineHeight: '28px',
                                                            maxHeight:  '400px',
                                                            overflowY:  'auto',
                                                        }}
                                                    >
                                                        {!isQuillContentEmpty(form.description) ? (
                                                            <div
                                                            className="ql-editor"
                                                            dangerouslySetInnerHTML={{ __html: cleanQuillHtml(form.description) }}

                                                        />
                                                        ) : (
                                                            <p className="text-muted fst-italic">
                                                                Start typing the description to see it rendered here exactly
                                                                as it will appear on the published blog page.
                                                            </p>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>{/* /col-lg-6 right */}

                                    </div>{/* /row g-4 */}
                                </>
                            )}

                            {/* ════ DELETE MODAL ════ */}
                            <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
                                <Modal.Header closeButton>
                                    <Modal.Title>Delete Blog</Modal.Title>
                                </Modal.Header>
                                <Modal.Body>
                                    Are you sure you want to delete{' '}
                                    <strong>{selectedRow?.title}</strong>?
                                </Modal.Body>
                                <Modal.Footer>
                                    <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
                                        Cancel
                                    </Button>
                                    <Button variant="danger" onClick={handleDelete}>Yes, Delete</Button>
                                </Modal.Footer>
                            </Modal>

                        </div>
                        <Footer />
                    </div>
                )}
            </div>
        </div>
    );
};

export default Blogs;
